import { Trash, Plus } from 'phosphor-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BuyerUserPermissions, DEFAULT_BUYER_USER_PERMISSIONS } from '@dill/dill-shared';
import { DillButton, DillModal, DillToggle } from '../../../../general/Dill';
import { AppInputField } from '../../../../general/AppInputField/AppInputField';
import { isEmailValid, buildSupplierPath } from '../../../../../utils/helpers';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { supplierInviteUsersForBuyer } from '../../../../../redux/services/buyersService';
import {
  closeMessageModal,
  openMessageModal,
} from '../../../../../redux/globalSlices/genericSlice';

type ReferredUser = {
  firstName: string;
  lastName: string;
  email: string;
  permissions: BuyerUserPermissions;
};

const createDefaultReferredUser = (): ReferredUser => ({
  firstName: '',
  lastName: '',
  email: '',
  permissions: DEFAULT_BUYER_USER_PERMISSIONS,
});

function SupplierCustomerInviteUserModal({
  open,
  selectedBuyerId,
  handleClose,
}: {
  open: boolean;
  selectedBuyerId: string | null;
  handleClose: () => void;
}) {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const navigate = useNavigate();
  const [contactList, setContactList] = useState<ReferredUser[]>([]);
  const [isShowValidationErrors, setIsShowValidationErrors] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const columns = useMemo(() => {
    return [
      { id: 'firstName', name: 'First Name', width: 250, cellType: 'VIEW_TEXT' },
      { id: 'lastName', name: 'Last Name', width: 250, cellType: 'VIEW_TEXT' },
      { id: 'email', name: 'Email', width: 300, cellType: 'VIEW_TEXT' },
      { id: 'permissions', name: 'Permissions', width: 260, cellType: 'VIEW_TEXT' },
      { id: 'actions', name: '', width: 100, cellType: 'VIEW_TEXT' },
    ];
  }, []);

  const totalWidth = useMemo(
    () =>
      columns.reduce((curr, prev) => {
        return curr + prev.width;
      }, 0),
    [columns]
  );

  const handleEditContactList = (
    position: number,
    key: keyof ReferredUser,
    value: string | BuyerUserPermissions
  ) => {
    setContactList([
      ...contactList.map((item, i) => {
        if (i === position) {
          return { ...item, [key]: value };
        }
        return { ...item };
      }),
    ]);
  };

  const handleDeleteContactFromList = (position: number) => {
    setContactList([...contactList.filter((item, i) => i !== position)]);
  };

  const handleAddContactToList = () => {
    setContactList([...contactList, createDefaultReferredUser()]);
  };

  useEffect(() => {
    if (contactList.length === 0) {
      handleAddContactToList();
    }
  }, [contactList]);

  const checkContactsForErrors = () => {
    let isError = false;
    contactList.forEach((contact) => {
      const { permissions, ...fieldsToValidate } = contact;
      const emptyValue = Object.values(fieldsToValidate).filter((value) => !value);
      if (emptyValue.length > 0) {
        isError = true;
      }
      if (!isEmailValid(contact.email)) {
        isError = true;
      }
    });
    return isError;
  };

  const handleSubmitReferral = async () => {
    setErrorMessage('');
    if (contactList.length === 0 || checkContactsForErrors()) {
      setIsShowValidationErrors(true);
      return;
    } else {
      setIsShowValidationErrors(false);
    }
    if (!selectedBuyerId) {
      setErrorMessage('Buyer not selected');
      return;
    }
    dispatch(
      openMessageModal({
        buttonText: '',
        modalType: 'LOADING',
        title: 'Inviting',
        subTitle: 'Inviting users',
      })
    );
    const response = await dispatch(
      supplierInviteUsersForBuyer({
        supplierId: currentSupplier?.id || '',
        buyerId: selectedBuyerId,
        invitedUsers: contactList.map((contact) => ({
          firstName: contact.firstName.trim(),
          lastName: contact.lastName.trim(),
          email: contact.email.trim(),
          permissions: contact.permissions || DEFAULT_BUYER_USER_PERMISSIONS,
        })),
      })
    );

    if (response.type === 'buyers/supplierInviteUsersForBuyer/fulfilled') {
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'SUCCESS',
          title: 'Users Added',
          subTitle: `Users Added successfully`,
          onClose: () => {
            navigate(buildSupplierPath(currentSupplier?.id, `/customers/${selectedBuyerId}`));
            handleClose();
          },
        })
      );
    } else if (response.type === 'buyers/supplierInviteUsersForBuyer/rejected') {
      const responseErrorMessage = response.payload as string;
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Error inviting users',
          subTitle: `${responseErrorMessage} Contact (628) 304-0931 for customer support`,
        })
      );
    } else {
      closeMessageModal();
    }
  };

  return (
    <DillModal
      open={open}
      onClose={() => handleClose()}
      title="Add Contacts"
      maxWidth="840px"
      footer={
        <>
          <DillButton label="Close" variant="secondary" onClick={() => handleClose()} />
          <DillButton label="Add New" onClick={() => handleSubmitReferral()} />
        </>
      }>
      <div className="w-full bg-gray-50 p-4 rounded-xl flex flex-col">
        <div className="h-fit max-h-60 flex flex-col overflow-y-auto py-1 w-full">
          {contactList.length > 0 &&
            contactList.map((contact, i) => {
              return (
                <div key={i + 'op'} className="w-full flex">
                  {columns.map((column, j) => {
                    const columnId = column.id as keyof ReferredUser;
                    const rawValue = contact[columnId];
                    const textValue = typeof rawValue === 'string' ? rawValue : '';
                    return (
                      <div
                        key={j + 'oo'}
                        style={{ flex: column.width / totalWidth }}
                        className="px-1 py-2 items-center">
                        {column.id === 'email' && (
                          <AppInputField
                            label={column.name}
                            isRequired={true}
                            value={textValue}
                            onTextChange={(text) =>
                              handleEditContactList(i, columnId, text.toLowerCase().trim())
                            }
                            errorText={
                              isShowValidationErrors && !textValue
                                ? 'Required'
                                : isShowValidationErrors && !isEmailValid(textValue)
                                ? 'Email is not valid'
                                : ''
                            }
                          />
                        )}
                        {column.id !== 'actions' &&
                          column.id !== 'email' &&
                          column.id !== 'permissions' && (
                            <AppInputField
                              label={column.name}
                              isRequired={true}
                              value={textValue}
                              onTextChange={(text) => handleEditContactList(i, columnId, text)}
                              errorText={isShowValidationErrors && !textValue ? 'Required' : ''}
                            />
                          )}
                        {column.id === 'permissions' && (
                          <div className="flex flex-col h-full justify-end">
                            <label className="text-xs font-medium text-gray-500 mb-1.5">
                              Permissions
                            </label>
                            <div className="flex items-center gap-3">
                              <div className="flex flex-col items-center gap-0.5 opacity-50 cursor-not-allowed">
                                <DillToggle value onChange={() => {}} />
                                <span className="text-[10px] text-gray-400">Viewer</span>
                              </div>
                              <div className="flex flex-col items-center gap-0.5">
                                <DillToggle
                                  value={(
                                    contact.permissions || DEFAULT_BUYER_USER_PERMISSIONS
                                  ).includes('PAYER')}
                                  onChange={(v) =>
                                    handleEditContactList(
                                      i,
                                      'permissions',
                                      v ? ['VIEWER', 'PAYER'] : ['VIEWER']
                                    )
                                  }
                                />
                                <span className="text-[10px] text-gray-400">Payer</span>
                              </div>
                            </div>
                          </div>
                        )}
                        {column.id === 'actions' && (
                          <div className="h-full flex items-end">
                            <DillButton
                              variant="icon-red"
                              icon={<Trash size={16} weight="bold" />}
                              onClick={() => handleDeleteContactFromList(i)}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
        </div>
        <DillButton
          label="Add Contact"
          variant="ghost-blue"
          icon={<Plus size={16} weight="bold" />}
          onClick={handleAddContactToList}
        />
      </div>
      {errorMessage && <p className="text-xs text-red-500">{errorMessage}</p>}
    </DillModal>
  );
}

export default SupplierCustomerInviteUserModal;
